import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { Typography, Tooltip, IconButton, Fab } from '@material-ui/core';
import { Refresh, Add, Delete } from '@material-ui/icons';
import MaterialTable from 'material-table';
import { MuiTableRows } from '../../../themes';
import { BackButton, hasPermission, localization, getDimensionsOfPagination, hasAtLeastOnePermission } from '../../sc-utilities';
import Toolbar from '../../sc-utilities/Toolbar'
import * as actions from './store/erasureMethodActions';
import * as action from '../../np-core/Auth/store/authActions';
import ErasureMethodDelete from './ErasureMethodDelete';
import * as Yup from "yup";
import Export from '../../sc-components/ExportForm';

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        },
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16
    },
    customToolbar: {
        marginLeft: 14,
        width: "900px"
    }
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];
let initialValues = [
    'Key',
    'LongName',
    'ShortName',
    'SupportedClientTypes',
    'DedicatedFor',
    'Description',
    'Visible',
];

const route = 'erasure-method';

const ErasureMethodList = props => {
    const { classes, translations, deleting, onFetchErasureMethod, onErasureMethodCount, fetching, saveColumnsFilter, activeCustomerID, tableColumns, onBulkDeleteErasureMethods, permissions, redirectHandler, tableRef } = props;
    const [deleteModalState, setDeleteModalState] = useState({ open: false, erasureMethodIDs: [] });
    const columnObject = useMemo(() => tableColumns
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []);

    const windowHeight = hasPermission(permissions, 'erasure_method_export') ? 235 : 197;
    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        selection: hasAtLeastOnePermission(permissions, ['erasure_method_delete']),
        search: false,
        minBodyHeight: window.innerHeight - windowHeight < 300 ? 300 : window.innerHeight - windowHeight,
        maxBodyHeight: window.innerHeight - windowHeight,
        pageSizeOptions: rowsPageSizeInit,
        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? MuiTableRows.odd : MuiTableRows.even };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    }

    async function fetchTableData(query) {
        let q = filterQuery(query.filters);
        query['ActiveCustomerID'] = activeCustomerID;
        query['q'] = q;
        let count = await onErasureMethodCount(query);
        if (!query['page'] && query['pageSize'] !== 20 && query['pageSize'] !== 100) {
            let dimensions = getDimensionsOfPagination(count)
            query['pageSize'] = dimensions.pageSize
            rowsPageSizeInit = dimensions.rowsPageSize
            pageSize = dimensions.pageSize
        }
        let result = count ? await onFetchErasureMethod(query) : [];
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result, page: query.page, pageSize: pageSize, totalCount: count };
    }

    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography className={classes.typography}>{translations["securaze_standards"] || 'Securaze Standards'}</Typography>
                {hasPermission(permissions, 'erasure_method_create') ? <Fab
                    variant="extended"
                    size="small"
                    color="primary"
                    aria-label="Add"
                    style={{ borderRadius: '1px' }}
                    className={classes.fab}
                    onClick={() => redirectHandler(`/admin/erasureMethod/create`)}
                >
                    <Add size="small" />
                    {translations["create_new"] || "Create new"}
                </Fab> : null}
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" disabled={fetching} onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </div>
            {
                hasPermission(permissions, 'erasure_method_export') ?
                    <Export
                        route={route}
                        tableRef={tableRef}
                        initialValues={initialValues}
                        formSettings={formSettings}
                        showVisibilities={true}
                         /> : null
            }
        </div>
    }

    const handleCloseSafe = () => {
        setDeleteModalState({ open: false, erasureMethodIDs: [] });
    }

    const handleCloseDanger = async () => {
        await onBulkDeleteErasureMethods({ ErasureMethodIDs: deleteModalState.erasureMethodIDs, ActiveCustomerID: activeCustomerID });
        setDeleteModalState({ open: false, erasureMethodIDs: [] });
        tableRef.current && tableRef.current.onQueryChange()
    };

    const handleBulkDelete = erasureMethodIDs => {
        setDeleteModalState({ ...deleteModalState, open: true, erasureMethodIDs: erasureMethodIDs });
    };

    const columnHidden = newColumnState => {
        let objToSendColumn = {}
        let objColumns = {}
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden
            return objColumns
        })
        objToSendColumn[`ErasureMethod`] = objColumns
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomerID })
        return newColumnState
    }

    const tableActions = [
        {
            tooltip: `${translations["remove_all_selected"] || "Remove all selected"} ${translations["securaze_standards"] || 'Securaze Standards'}`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map(erasureMethod => erasureMethod.ErasureMethodID))
        },
    ];

    let formSettings = {
        Validation: initialValues.reduce((o, k) => ({...o, [k]: Yup.boolean()}), {}),
        ButtonSubmit: hasPermission(permissions, "erasure_method_export") ? {
            Title: translations["download"] || "Download",
            Color: 'primary'
        } : null
    };

    return (
        <>
            <MaterialTable
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                isLoading={fetching}
                components={{
                    Toolbar: props => (
                        <Toolbar data={props} actions={tableActions} selectedRows={props.selectedRows} translations={translations} />
                    )
                }}
                localization={localization(translations)}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
            {hasPermission(permissions, 'erasure_method_delete') ? (
                <ErasureMethodDelete
                    entityName={translations["securaze_standards"] || 'Securaze Standards'}
                    translations={translations}
                    deleting={deleting}
                    handleCloseDanger={handleCloseDanger}
                    handleCloseSafe={handleCloseSafe}
                    deleteModalState={deleteModalState}
                />
            ) : null}
        </>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        permissions: state?.core?.login?.permissions || [],
        fetching: state.admin.erasureMethod.fetching,
        deleting: state.admin.erasureMethod.deleting
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onErasureMethodCount: filter => dispatch(actions.fetchErasureMethodCount(filter)),
        onFetchErasureMethod: filter => dispatch(actions.fetchErasureMethod(filter)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
        onBulkDeleteErasureMethods: data => dispatch(actions.bulkDeleteErasureMethods(data))
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(ErasureMethodList));


